import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isGuest, useAuth } from '@/stores/auth';
import type { Role } from '@/api/types';
import { FullScreenLoader } from '@/components/ui/States';
import { peekReturnTo, setReturnTo } from '@/lib/returnTo';

/** Logged out → /login (remembering where they were going). */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const loc = useLocation();
  if (status === 'booting') return <FullScreenLoader />;
  if (status === 'anon') return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children}</>;
}

/**
 * Authenticated but not onboarded → /onboarding. A /try host (docs/SPEC_TRY.md)
 * signed in without onboarding and may open their date's lobby first: they
 * come back to it afterwards (unless an invite link already set where to go).
 */
export function RequireOnboarded({ children }: { children: ReactNode }) {
  const me = useAuth((s) => s.me);
  const loc = useLocation();
  // Guest accounts (docs/SPEC_TRY.md) have their date pages and nothing else.
  if (isGuest(me) && !GUEST_PATHS.some((p) => loc.pathname.startsWith(p))) return <Navigate to="/guest" replace />;
  if (me && !me.onboarded) {
    if (!peekReturnTo() && loc.pathname.startsWith('/dates/')) setReturnTo(loc.pathname);
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/** Where a guest account may go inside the app: its date lobby and the call. */
const GUEST_PATHS = ['/dates/', '/call/'];

/** Onboarding is only for people who haven't finished it. */
export function RequireNotOnboarded({ children }: { children: ReactNode }) {
  const me = useAuth((s) => s.me);
  // v6.1: an invite link sent them here — go back to it (the invite page clears it).
  if (me?.onboarded) return <Navigate to={peekReturnTo() ?? '/home'} replace />;
  return <>{children}</>;
}

export function RequireRole({ roles, children, fallback }: { roles: Role[]; children: ReactNode; fallback?: ReactNode }) {
  const me = useAuth((s) => s.me);
  if (!me || !roles.includes(me.role)) return <>{fallback ?? <Navigate to="/home" replace />}</>;
  return <>{children}</>;
}

/** Landing/login/signup: signed-in people go straight in. */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { status, me } = useAuth();
  const loc = useLocation();
  if (status === 'booting') return <FullScreenLoader />;
  // A guest sees the landing and sign-in pages like anyone signed out (signing in upgrades it).
  if (status === 'authed' && me && !isGuest(me)) {
    const from = (loc.state as { from?: string } | null)?.from;
    return <Navigate to={me.onboarded ? (from && from !== '/' ? from : (peekReturnTo() ?? '/home')) : '/onboarding'} replace />;
  }
  return <>{children}</>;
}
